import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import AgendaHuecos from '../ordenes/AgendaHuecos';
import { SelectorConfirmacion, EtiquetaTentativa } from '../ordenes/FechaTentativa';
import { LuCircleCheck, LuCalendar, LuClock, LuMessageCircle } from 'react-icons/lu';
import ModalShell from '../ui/ModalShell';
import { datosOrdenDesdePresupuesto } from '../../utils/ordenes';
import { fechaAR, getTodayISO, formatDateISO } from '../../utils/dateUtils';
import AvisoHoraPasada, { horarioPasado } from '../ordenes/AvisoHoraPasada';
import HoraRango from '../ordenes/HoraRango';
import { horaTexto } from '../../utils/ordenes';

const PRIORIDADES = [
    { value: 'NORMAL',  label: 'Normal'  },
    { value: 'ALTA',    label: 'Alta'    },
    { value: 'URGENTE', label: 'Urgente' },
];




const LABEL = 'block text-label font-black text-muted uppercase tracking-widest mb-1.5';

/**
 * ModalDespacharPresupuesto (29-oct-2026: mismo formato que "Agendar visita" de los pedidos de empresa:
 * técnico arriba, tira de días y franjas; una sola forma de agendar en toda la app)
 * Sheet rápido para crear una orden de visita a partir de un presupuesto existente.
 * Solo pide técnico, fecha, hora y prioridad. El resto se pre-llena del presupuesto.
 */
export default function ModalDespacharPresupuesto({ presupuesto, calcularTotal, onCerrar, onDespachado, onYaHecho }) {
    const [tecnicos,    setTecnicos]    = useState([]);
    const [cargando,    setCargando]    = useState(true);
    const [guardando,   setGuardando]   = useState(false);
    const [ordenCreada, setOrdenCreada] = useState(null);

    const total = calcularTotal(presupuesto);

    const [form, setForm] = useState({
        tecnicoId:       '',
        fechaProgramada: '',
        horaEstimada:    '',
        franja:          '',
        prioridad:       'NORMAL',
        // Presupuesto que el cliente todavía no aprobó → la fecha arranca como tentativa
        sinConfirmar:    presupuesto?.estado === 'PRESUPUESTO',
    });
    const [ordenesAgenda, setOrdenesAgenda] = useState([]);
    useEffect(() => {
        const d = new Date(); d.setDate(d.getDate() + 120);
        const antes = new Date(); antes.setDate(antes.getDate() - 30);
        api.get('/ordenes', { params: { desde: formatDateISO(antes), hasta: formatDateISO(d) } })
            .then(r => setOrdenesAgenda(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    }, []);

    useEffect(() => {
        api.get('/ordenes/tecnicos')
            .then(r => setTecnicos(r.data || []))
            .catch(() => {})
            .finally(() => setCargando(false));
    }, []);

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    // 2-nov-2026: un día que ya pasó = el trabajo ya se hizo (faltaba cargarlo): la visita queda hecha ese día, el
    // trabajo con esa fecha y a nombre del técnico, y se abre el cierre para cargar cómo se cobró
    const yaPaso = (!!form.fechaProgramada && form.fechaProgramada < getTodayISO())
        || (!!form.franja && !!horarioPasado(form.fechaProgramada || getTodayISO(), form.horaEstimada || form.franja));
    const guardarHecho = async () => {
        setGuardando(true);
        const t = toast.loading('Cargando…');
        try {
            const res = await api.post('/ordenes', { ...datosOrdenDesdePresupuesto(presupuesto, {
                tecnicoId: form.tecnicoId, fechaProgramada: form.fechaProgramada || getTodayISO(),
                horaEstimada: form.horaEstimada || form.franja || '', prioridad: 'NORMAL',
            }), sinConfirmar: false });
            await api.patch(`/ordenes/${res.data.id}/estado`, { estado: 'COMPLETADA' });
            const s = await api.patch(`/servicios/${presupuesto.id}/estado`, { estado: 'COMPLETADO', fechaServicio: form.fechaProgramada || getTodayISO() });
            toast.success('Listo: ahora cargá cómo se cobró', { id: t });
            if (onDespachado) onDespachado(res.data);
            if (onYaHecho) onYaHecho(s.data || presupuesto); else onCerrar();
        } catch (e) {
            toast.error(e?.response?.data?.mensaje || 'No se pudo cargar', { id: t });
        } finally { setGuardando(false); }
    };

    const handleGuardar = async () => {
        if (!form.tecnicoId)       { toast.error('Seleccioná un técnico');  return; }
        if (yaPaso) { guardarHecho(); return; }
        setGuardando(true);
        try {
            const res = await api.post('/ordenes', { ...datosOrdenDesdePresupuesto(presupuesto, {
                tecnicoId: form.tecnicoId, fechaProgramada: form.fechaProgramada || getTodayISO(),
                horaEstimada: form.horaEstimada || form.franja || '', prioridad: form.prioridad,
            }), sinConfirmar: form.sinConfirmar });
            setOrdenCreada(res.data);
            toast.success('Orden de visita creada');
            // El presupuesto pasa a "en progreso": ya no es un pendiente suelto, está
            // asignado a un técnico. Antes se quedaba huérfano en estado PRESUPUESTO
            // para siempre y la pantalla de Presupuestos lo marcaba "Ejecutado" solo
            // por una búsqueda cruzada — ahora el estado real refleja lo que pasó.
            try {
                await api.patch(`/servicios/${presupuesto.id}/estado`, { estado: 'EN_PROGRESO' });
            } catch {
                console.error('No se pudo pasar el presupuesto a EN_PROGRESO');
            }
            if (onDespachado) onDespachado(res.data);
        } catch (e) {
            // Mostrar el motivo real que manda el servidor (6-oct-2026)
            toast.error(e?.response?.data?.mensaje || e?.response?.data?.message || 'Error al crear la orden');
        } finally {
            setGuardando(false);
        }
    };

    const tecnicoAsignado = tecnicos.find(t => String(t.id) === String(form.tecnicoId));

    const abrirWhatsApp = () => {
        const num = (tecnicoAsignado?.whatsapp || tecnicoAsignado?.telefono || '').replace(/\D/g, '');
        const msg = encodeURIComponent(
            `🔧 *Nuevo trabajo asignado*\n` +
            `Cliente: ${presupuesto.clienteNombre || '-'}\n` +
            (presupuesto.sedeDireccion ? `Dirección: ${presupuesto.sedeDireccion}\n` : '') +
            `Fecha: ${fechaAR(form.fechaProgramada)}${form.horaEstimada ? ` ${String(form.horaEstimada).includes('-') ? 'de ' + horaTexto(form.horaEstimada) : 'a las ' + form.horaEstimada}` : form.franja ? ` de ${horaTexto(form.franja)}` : ''}\n` +
            (form.sinConfirmar ? `⏳ FECHA TENTATIVA: el cliente todavía no la confirmó. No vayas hasta que te avise.\n` : '') +
            `Prioridad: ${form.prioridad}\n` +
            `Monto estimado: $${total.toLocaleString('es-AR')}`
        );
        window.open(`https://wa.me/${num}?text=${msg}`, '_blank');
    };

    const subtitulo = [presupuesto.clienteNombre, presupuesto.sedeNombre, presupuesto.sedeDireccion].filter(Boolean).join(' · ')
        + (total ? ` · $${total.toLocaleString('es-AR')}` : '');
    const listo = !!(form.tecnicoId && form.franja);

    if (ordenCreada) {
        return (
            <ModalShell titulo="Visita agendada" subtitulo={subtitulo} onCerrar={onCerrar} ancho="md:max-w-2xl"
                pie={
                    <div className="flex gap-2">
                        <button type="button" onClick={abrirWhatsApp}
                            className="flex-[2] h-12 rounded-xl font-black text-body text-white bg-[#25D366] active:scale-95 inline-flex items-center justify-center gap-1.5">
                            <LuMessageCircle size={16} /> Avisar a {tecnicoAsignado?.nombre?.split(' ')[0] || 'técnico'}
                        </button>
                        <button type="button" onClick={onCerrar} className="flex-1 h-12 rounded-xl bg-chip text-secondary font-black text-body active:scale-95">Listo</button>
                    </div>
                }>
                <div className="space-y-3">
                    <div className="py-4 text-center">
                        <div className="flex justify-center mb-2"><LuCircleCheck size={36} className="text-[#1E8A4A]" /></div>
                        <p className="text-body font-bold text-ink">
                            {tecnicoAsignado?.nombre || 'Técnico'} · {fechaAR(form.fechaProgramada)}{form.horaEstimada ? ` ${String(form.horaEstimada).includes('-') ? 'de ' + horaTexto(form.horaEstimada) : 'a las ' + form.horaEstimada}` : ''}
                        </p>
                        {form.sinConfirmar && <div className="mt-2 flex justify-center"><EtiquetaTentativa /></div>}
                    </div>
                    <div className="flex items-center justify-center gap-3 text-caption text-muted">
                        <span className="inline-flex items-center gap-1"><LuCalendar size={12} />{fechaAR(form.fechaProgramada)}</span>
                        {form.horaEstimada && <span className="inline-flex items-center gap-1"><LuClock size={12} />{form.horaEstimada}</span>}
                        <span className="capitalize">{form.prioridad.toLowerCase()}</span>
                    </div>
                </div>
            </ModalShell>
        );
    }

    return (
        <ModalShell titulo="Agendar visita" subtitulo={subtitulo} onCerrar={onCerrar} ancho="md:max-w-2xl"
            pie={<button type="button" onClick={handleGuardar} disabled={!listo || guardando}
                className="w-full h-12 rounded-xl bg-[#C9341F] text-white font-black text-body active:scale-95 disabled:opacity-40">
                {guardando ? 'Guardando…' : yaPaso ? 'Ya se hizo: cargar el cobro' : 'Agendar'}</button>}>
            {cargando ? (
                <p className="py-8 text-center text-muted text-body">Cargando técnicos…</p>
            ) : (
                <div className="space-y-3">
                    <AgendaHuecos tecnicos={tecnicos} ordenes={ordenesAgenda}
                        fecha={form.fechaProgramada || getTodayISO()}
                        onFecha={v => set('fechaProgramada', v)}
                        hueco={form.tecnicoId ? { tecnicoId: Number(form.tecnicoId), franja: form.franja || null } : null}
                        onTecnico={t => setForm(f => ({ ...f, tecnicoId: String(t.id) }))}
                        onHueco={h => setForm(f => ({ ...f, tecnicoId: h ? String(h.tecnicoId) : '', franja: h?.franja || '', fechaProgramada: f.fechaProgramada || getTodayISO() }))}
                        direccion={presupuesto.sedeDireccion} permitirPasado />
                    {listo && (
                        <div className="p-3.5 rounded-2xl bg-chip space-y-2">
                            <p className="text-body text-ink">
                                <b>{tecnicoAsignado?.nombre?.split(' ')[0] || 'Técnico'}</b> · {new Date((form.fechaProgramada || getTodayISO()) + 'T12:00:00').toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'numeric' })} · {horaTexto(form.horaEstimada || form.franja)}
                            </p>
                            <HoraRango value={form.horaEstimada} onChange={v => set('horaEstimada', v)} />
                            {!yaPaso && <AvisoHoraPasada fecha={form.fechaProgramada || getTodayISO()} hora={form.horaEstimada} franja={form.franja || null}
                                onTarde={(fr) => setForm(f => ({ ...f, franja: fr, horaEstimada: '' }))}
                                onManana={(d) => setForm(f => ({ ...f, fechaProgramada: d, horaEstimada: '' }))} />}
                        </div>
                    )}
                    {yaPaso && (
                        <p className="p-3 rounded-xl bg-[rgba(212,136,0,0.12)] text-caption font-bold text-[#B45309] dark:text-[#F0A500]">
                            Es un día que ya pasó: queda como trabajo hecho ese día, a nombre de {tecnicoAsignado?.nombre?.split(' ')[0] || 'quien elijas'}. Después cargás cómo se cobró.
                        </p>
                    )}
                    {!yaPaso && <SelectorConfirmacion tentativa={form.sinConfirmar} onChange={v => set('sinConfirmar', v)} />}
                    {!yaPaso && <div>
                        <p className={LABEL}>Prioridad</p>
                        <div className="flex gap-2">
                            {PRIORIDADES.map(p => (
                                <button key={p.value} type="button" onClick={() => set('prioridad', p.value)} aria-pressed={form.prioridad === p.value}
                                    className={`flex-1 h-10 rounded-xl text-label font-black active:scale-95 ${form.prioridad === p.value ? 'bg-brand-red text-white' : 'bg-chip text-secondary'}`}>
                                    {p.label}
                                </button>
                            ))}
                        </div>
                    </div>}
                </div>
            )}
        </ModalShell>
    );
}
